import { runInAction } from "mobx";
import { PlayIcon, RotateCcw } from "lucide-react";
import { observer } from "mobx-react-lite";

import {
  ANALYTICS_TAB_INDEX,
  RUN_QUERY_SPINNER_SIZE,
} from "@/components/RequestsAnalytics/constants";
import LoadingSpinner from "@/components/LoadingSpinner";
import { StructuredSearchPageHeader } from "@/components/StructuredSearch/StructuredSearchPageHeader";
import { TimezoneDateRangeControls } from "@/components/StructuredSearch/TimezoneDateRangeControls";
import { useSessionStorageReturnNavigation } from "@/components/StructuredSearch/hooks/useSessionStorageReturnNavigation";
import { useStructuredSearchStore } from "@/components/StructuredSearch";
import {
  areAnalyticsQueriesEqual,
  createAnalyticsQueryState,
  useAnalyticsQuery,
} from "@/components/RequestsAnalytics/analytics-query-context";
import { Button } from "@/components/ui/button";
import { SESSION_STORAGE } from "@/constants";
import { appendTracesDrawerIntentToPath } from "@/utils/tracesUrlParams";

export const TracesAnalyticsHeaderControls = observer(
  ({
    selectedTabIndex,
    minDate,
    maxDate,
  }: {
    selectedTabIndex: number;
    minDate?: Date;
    maxDate?: Date;
  }) => {
    const structuredSearchStore = useStructuredSearchStore();
    const {
      isSearchEndpointRunning,
      previousQueryState,
      queryHistoryCount,
      queryState,
      restoreQueryState,
      runQuery,
    } = useAnalyticsQuery();
    const currentQueryState = createAnalyticsQueryState({
      filters: structuredSearchStore.filters,
      dateRange: structuredSearchStore.dateRange,
      q: structuredSearchStore.q,
    });
    const hasPendingQueryChanges = !areAnalyticsQueriesEqual(queryState, currentQueryState);
    const undoQueryState = hasPendingQueryChanges ? queryState : previousQueryState;
    const undoHistorySuffix = queryHistoryCount > 1 ? ` (${queryHistoryCount} available)` : "";
    const undoButtonTitle = hasPendingQueryChanges
      ? "Undo unapplied query changes"
      : `Undo last query change${undoHistorySuffix}`;
    const undoPendingQueryChanges = () => {
      if (!undoQueryState) {
        return;
      }

      runInAction(() => {
        structuredSearchStore.cancelEdit();
        structuredSearchStore.setFiltersFromURL(undoQueryState.filters);
        structuredSearchStore.setDateRange(undoQueryState.dateRange);
        structuredSearchStore.setSearch(undoQueryState.q);
        if (!hasPendingQueryChanges) {
          restoreQueryState(undoQueryState, { consumeHistory: true });
        }
      });
    };

    const analyticsControls =
      selectedTabIndex === ANALYTICS_TAB_INDEX ? (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={undoPendingQueryChanges}
            disabled={!undoQueryState || isSearchEndpointRunning}
            className="h-10 gap-2 px-4"
            title={undoButtonTitle}
          >
            <RotateCcw className="h-4 w-4" />
            Undo
          </Button>
          <Button
            type="button"
            onClick={runQuery}
            disabled={!hasPendingQueryChanges || isSearchEndpointRunning}
            className={`h-10 gap-2 px-4 ${
              hasPendingQueryChanges && !isSearchEndpointRunning
                ? "animate-pulse [animation-duration:3s]"
                : ""
            }`}
          >
            {isSearchEndpointRunning ? (
              <>
                <LoadingSpinner size={RUN_QUERY_SPINNER_SIZE} />
                Running
              </>
            ) : (
              <>
                <PlayIcon className="h-4 w-4" />
                Run query
              </>
            )}
          </Button>
        </>
      ) : null;

    return (
      <TimezoneDateRangeControls
        minDate={minDate}
        maxDate={maxDate}
        onDateRangeChange={range => structuredSearchStore.setDateRange(range)}
        trailingControls={analyticsControls}
      />
    );
  },
);

export const TracesAnalyticsPageHeader = ({
  selectedTabIndex,
  title = "Traces & Analytics",
  minDate,
  maxDate,
  showBackButton = true,
}: {
  selectedTabIndex: number;
  title?: string;
  minDate?: Date;
  maxDate?: Date;
  showBackButton?: boolean;
}) => {
  const handleBack = useSessionStorageReturnNavigation({
    storageKey: SESSION_STORAGE.TRACES_RETURN_URL,
    transformReturnUrl: appendTracesDrawerIntentToPath,
  });

  return (
    <StructuredSearchPageHeader
      title={title}
      showBackButton={showBackButton}
      onBack={handleBack}
      controls={
        <TracesAnalyticsHeaderControls
          selectedTabIndex={selectedTabIndex}
          minDate={minDate}
          maxDate={maxDate}
        />
      }
    />
  );
};
